import React, { useEffect } from 'react';
import cx from 'classnames';
import type { WPreviewPhotoView } from '@zola/svc-web-api-ts-client';
// Selectors
import { useSelector } from 'react-redux';
import { getUserWeddingDetails } from 'selectors/user/userSelectors';
// Components
import { ButtonV2 } from '@zola/zola-ui/src/components/Button';
import LinkV2 from '@zola/zola-ui/src/components/LinkV2/LinkV2';
import SamplePreviewCustom from 'paper/components/SamplePreviewCustom';
// Util
import {
  trackCtaClicked,
  trackCtaViewed,
  CTA_TYPES,
  CtaEventDataType,
  onboardTrackingConstants,
} from '@zola-helpers/client';
// Hooks
import useTemplatesFromCardSuite from 'cards/components/common/SingleSample/util/hooks/useTemplatesFromCardSuite';
import type { SamplePreviewCustomProps } from 'paper/components/SamplePreviewCustom/SamplePreviewCustom';
import usePreviewPhoto from '../../../../../../../lib/hooks/usePreviewPhoto';
// Types
import type {
  SingleSampleAccessPointUIProps,
  CardSuiteInfoSummary,
  CustomTemplateSummary,
} from '../../../util/types';
// Styles
import styles from './APSamplePreviewModule.module.less';

export interface APSamplePreviewModuleProps
  extends SingleSampleAccessPointUIProps,
    Pick<
      SamplePreviewCustomProps,
      'samplePreviewPropOverrides' | 'productColorPickerPropOverrides'
    > {
  cardSuiteInfo?: CardSuiteInfoSummary;
  hed: string;
  trackingParams: Pick<CtaEventDataType, 'section' | 'cta_id' | 'location'>;
  backgroundColor?: string;
  photoUrl?: string;
  previewPhotoSelectionOverride?: WPreviewPhotoView.SourceEnum[];
  initialColor?: string;
  // Module applies vertical orientation at unique desktop sm screen sizes. This prop disables it
  disableResponsiveStyling?: { desktopSm?: boolean; mobileSm?: boolean };
  showSwatchArrow?: boolean;
  className?: string;
  samplePreviewCustomClassName?: string;
  ctaType?: 'button' | 'link';
}

const APSamplePreviewModule: React.FC<APSamplePreviewModuleProps> = ({
  handleOpenModal,
  cardSuiteInfo,
  hed,
  trackingParams,
  backgroundColor = 'blue',
  photoUrl,
  previewPhotoSelectionOverride,
  initialColor,
  samplePreviewPropOverrides,
  productColorPickerPropOverrides,
  showSwatchArrow,
  className,
  disableResponsiveStyling,
  samplePreviewCustomClassName,
  ctaType = 'button',
}) => {
  const weddingDetails = useSelector(state => getUserWeddingDetails(state));
  const customPreviewTemplates: CustomTemplateSummary[] | undefined = useTemplatesFromCardSuite(
    cardSuiteInfo,
    weddingDetails,
    3, // Only fetch first 3 colors
    initialColor
  );

  const isCustomPhotoTemplate =
    (
      customPreviewTemplates?.[0]?.pages?.[0]?.elements?.filter(
        element => element.content_type === 'IMAGE' && element.customizable
      ) || []
    ).length > 0;
  const shouldFetchPreviewPhotos = !photoUrl && isCustomPhotoTemplate;
  const previewPhoto = usePreviewPhoto(shouldFetchPreviewPhotos, previewPhotoSelectionOverride);
  const ctaText = isCustomPhotoTemplate ? 'Edit Your Photo' : 'Get Your Free Sample';

  const trackingPayload: CtaEventDataType = Object.assign(
    {
      business_unit: onboardTrackingConstants.BUSINESS_UNIT.INVITATIONS,
      group_id: 'SAMPLES_MODULE',
      cta_type: CTA_TYPES.BUTTON,
      position: 1,
      cta: ctaText,
    },
    trackingParams
  );

  useEffect(() => {
    trackCtaViewed(trackingPayload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = (): void => {
    trackCtaClicked(trackingPayload);
    const overrideProps = {
      entryPoint: trackingPayload.section || 'WEBSITE',
    };
    if (handleOpenModal) {
      handleOpenModal(overrideProps);
    }
  };
  const containerClasses = cx(styles.container, className, {
    [styles.containerResponsiveStylingDesktop]: !disableResponsiveStyling?.desktopSm,
    [styles.containerResponsiveStylingMobile]: !disableResponsiveStyling?.mobileSm,
    [styles.bgBlue]: backgroundColor === 'blue',
    [styles.bgPink]: backgroundColor === 'pink',
    [styles.bgPlum]: backgroundColor === 'plum',
  });
  const productPickerClasses = cx(
    productColorPickerPropOverrides?.className,
    styles.colorPickerTopSpacing,
    {
      [styles.colorPickerContainer]: showSwatchArrow,
      [styles[
        `colorPickerContainer--${customPreviewTemplates && customPreviewTemplates.length}`
      ]]: showSwatchArrow,
      [styles.colorPickerBottomSpacing]: !showSwatchArrow,
    }
  );

  return (
    <div className={containerClasses}>
      <div className={styles.innerContainer}>
        <div className={styles.textContainer}>
          <h3 className={styles.title}>{hed}</h3>
          <p className={styles.subtitle}>
            {`See our paper IRL, printed with your names and ${
              isCustomPhotoTemplate ? 'a photo you love' : 'wedding date'
            }.`}
          </p>
          {ctaType === 'button' && (
            <ButtonV2 compact variant="secondary" role="button" onClick={handleClick}>
              {ctaText}
            </ButtonV2>
          )}
          {ctaType === 'link' && (
            <LinkV2 className={styles.ctaLink} arrow onClick={handleClick}>
              {ctaText}
            </LinkV2>
          )}
        </div>
        <SamplePreviewCustom
          className={cx(styles.apEditWebsitePreviewContainer, samplePreviewCustomClassName)}
          customPreviewTemplates={customPreviewTemplates}
          customPhotoUrl={photoUrl || previewPhoto}
          onClick={handleClick}
          samplePreviewPropOverrides={samplePreviewPropOverrides}
          productColorPickerPropOverrides={{
            maxColorsToShow: 3,
            className: productPickerClasses,
            swatchWidth: productColorPickerPropOverrides?.swatchWidth,
          }}
        />
      </div>
    </div>
  );
};

export default APSamplePreviewModule;
